import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Link,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  Euro as EuroIcon,
  CalendarToday as CalendarIcon,
  Calculate as CalculateIcon,
  Functions as FunctionsIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../contexts/ApiContext';
import { HostingCalculator } from './HostingCalculator';
import { NiallCalcs } from './NiallCalcs';
import { NiallAccountLedger } from './NiallAccountLedger';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  equalFitCols,
  fitTruncateSx,
} from '../utils/fitTable';

const TAB_PATHS = [
  '/hosting/calculator',
  '/hosting/expenses',
  '/hosting/niall-calcs',
  '/hosting/niall-vrbo-account',
] as const;

interface HostExpense {
  id: number;
  date: string;
  property_id: number;
  property_nickname?: string;
  receipt_from: string;
  amount: number;
  notes?: string;
  image_link?: string;
  created_at: string;
  updated_at: string;
}

interface Property {
  id: number;
  nickname: string;
  address: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`hosting-tabpanel-${index}`}
      aria-labelledby={`hosting-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
}

export const HostExpenses: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tabFromPath = TAB_PATHS.indexOf(location.pathname as typeof TAB_PATHS[number]);
  const [tabValue, setTabValue] = useState(tabFromPath >= 0 ? tabFromPath : 0);
  const [expenses, setExpenses] = useState<HostExpense[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<HostExpense | null>(null);
  const [formData, setFormData] = useState({
    date: '',
    property_id: '',
    receipt_from: '',
    amount: '',
    notes: '',
    image_link: ''
  });

  useEffect(() => {
    fetchExpenses();
    fetchProperties();
  }, []);

  useEffect(() => {
    const idx = TAB_PATHS.indexOf(location.pathname as typeof TAB_PATHS[number]);
    setTabValue(idx >= 0 ? idx : 0);
  }, [location.pathname]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    navigate(TAB_PATHS[newValue]);
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/host-expenses/');
      setExpenses(response.data.expenses || []);
      setError('');
    } catch (err: any) {
      setError('Failed to load host expenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties/');
      setProperties(response.data.properties || []);
    } catch (err: any) {
      console.error('Failed to load properties:', err);
    }
  };

  const handleOpenDialog = (expense?: HostExpense) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        date: expense.date || '',
        property_id: expense.property_id.toString(),
        receipt_from: expense.receipt_from || '',
        amount: expense.amount.toString(),
        notes: expense.notes || '',
        image_link: expense.image_link || ''
      });
    } else {
      setEditingExpense(null);
      setFormData({
        date: '',
        property_id: '',
        receipt_from: '',
        amount: '',
        notes: '',
        image_link: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setFormData({
      date: '',
      property_id: '',
      receipt_from: '',
      amount: '',
      notes: '',
      image_link: ''
    });
  };

  const handleSubmit = async () => {
    try {
      const expenseData = {
        ...formData,
        property_id: parseInt(formData.property_id),
        amount: parseFloat(formData.amount)
      };

      if (editingExpense) {
        await api.put(`/api/host-expenses/${editingExpense.id}`, expenseData);
      } else {
        await api.post('/api/host-expenses/', expenseData);
      }

      await fetchExpenses();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save expense');
      console.error(err);
    }
  };

  const handleDelete = async (expenseId: number) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      await api.delete(`/api/host-expenses/${expenseId}`);
      await fetchExpenses();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete expense');
      console.error(err);
    }
  };

  const handleImportFiles = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        console.log('Host expenses import file selected:', file.name);

        try {
          setLoading(true);
          setError('');

          const formData = new FormData();
          formData.append('file', file);

          const response = await api.post('/api/host-expenses/import', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          console.log('Import successful:', response.data);

          await fetchExpenses();

          alert(`Successfully imported host expenses from ${file.name}`);
        } catch (err: any) {
          console.error('Import failed:', err);
          const errorData = err.response?.data;
          setError(errorData?.error || 'Failed to import host expenses');

          let errorMessage = errorData?.error || 'Unknown error';
          if (errorData?.debug_info) {
            errorMessage += `\n\nDebug Info:\n`;
            errorMessage += `Available columns: ${errorData.available_columns?.join(', ')}\n`;
            errorMessage += `Total rows: ${errorData.debug_info.total_rows}\n`;
            if (errorData.debug_info.column_details) {
              errorMessage += `Column details:\n`;
              errorData.debug_info.column_details.forEach((col: any) => {
                errorMessage += `- ${col.name} (${col.type}): ${col.sample_value}\n`;
              });
            }
          }

          alert(errorMessage);
        } finally {
          setLoading(false);
          input.value = '';
        }
      }
    };
    input.click();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 2, mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}
        >
          <Tab label="Hosting Fee Calculator" icon={<CalculateIcon />} iconPosition="start" />
          <Tab label="Hosting Expenses" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="Niall's Calcs" icon={<FunctionsIcon />} iconPosition="start" />
          <Tab label="Niall's VRBO Account" icon={<AccountBalanceIcon />} iconPosition="start" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <TabPanel value={tabValue} index={0}>
          <HostingCalculator />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={handleImportFiles}
              color="info"
            >
              Import Files
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              color="primary"
            >
              Add Expense
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : expenses.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No host expenses yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Start by adding your first expense or importing from a file.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleImportFiles}
                >
                  Import from File
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add First Expense
                </Button>
              </Box>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={equalFitCols(
                    ['date', 'property', 'receiptFrom', 'amount', 'notes', 'receipt', 'actions'],
                    { flexKey: 'notes' }
                  )}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Receipt From</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Receipt</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <CalendarIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                          <Box component="span" sx={fitTruncateSx}>
                            {formatDate(expense.date)}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <HomeIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={fitTruncateSx}
                            title={expense.property_nickname || `Property ${expense.property_id}`}
                          >
                            {expense.property_nickname || `Property ${expense.property_id}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={fitTruncateSx} title={expense.receipt_from}>
                          {expense.receipt_from}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, justifyContent: 'flex-end', minWidth: 0 }}>
                          <EuroIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
                          <Typography variant="body2" fontWeight="medium" sx={fitTruncateSx}>
                            {formatAmount(expense.amount)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={fitTruncateSx}
                          title={expense.notes || ''}
                        >
                          {expense.notes || 'No notes'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {expense.image_link ? (
                          <Link
                            href={expense.image_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={fitTruncateSx}
                          >
                            View Receipt
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={fitTruncateSx}>
                            No receipt
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell className="fit-table-no-clip">
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(expense)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(expense.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <NiallCalcs />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <NiallAccountLedger />
        </TabPanel>

        {/* Add/Edit Expense Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingExpense ? 'Edit Host Expense' : 'Add Host Expense'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                autoFocus
                margin="normal"
                label="Date"
                type="date"
                fullWidth
                variant="outlined"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Property</InputLabel>
                <Select
                  value={formData.property_id}
                  label="Property"
                  onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={property.id}>
                      {property.nickname} - {property.address}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="normal"
                label="Receipt From"
                fullWidth
                variant="outlined"
                value={formData.receipt_from}
                onChange={(e) => setFormData({ ...formData, receipt_from: e.target.value })}
                required
              />
              <TextField
                margin="normal"
                label="Amount"
                type="number"
                fullWidth
                variant="outlined"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                required
                inputProps={{ step: "0.01", min: "0" }}
              />
              <TextField
                margin="normal"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
              <TextField
                margin="normal"
                label="Image Link"
                fullWidth
                variant="outlined"
                value={formData.image_link}
                onChange={(e) => setFormData({ ...formData, image_link: e.target.value })}
                placeholder="URL to receipt image"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!formData.date || !formData.property_id || !formData.receipt_from || !formData.amount}
            >
              {editingExpense ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};
